import { createContext, useContext, useCallback, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/query-keys';

interface SubscriptionContextType {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  apiAddon: boolean;
  status: string;
  loading: boolean;
  checkSubscription: () => Promise<void>;
}

interface SubscriptionFields {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  apiAddon: boolean;
  status: string;
}

const defaultSubscriptionState: SubscriptionFields = {
  subscribed: false,
  productId: null,
  subscriptionEnd: null,
  apiAddon: false,
  status: 'inactive',
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const fetchSubscriptionFields = async (accessToken: string): Promise<SubscriptionFields> => {
  try {
    const { data, error } = await supabase.functions.invoke('sync-subscription', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (error) {
      const fallbackResponse = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (fallbackResponse.error) throw fallbackResponse.error;

      return {
        subscribed: fallbackResponse.data?.subscribed || false,
        productId: fallbackResponse.data?.product_id || null,
        subscriptionEnd: fallbackResponse.data?.subscription_end || null,
        apiAddon: fallbackResponse.data?.api_addon || false,
        status: fallbackResponse.data?.status || 'inactive',
      };
    }

    return {
      subscribed: data?.subscribed || false,
      productId: data?.product_id || null,
      subscriptionEnd: data?.subscription_end || null,
      apiAddon: data?.api_addon || false,
      status: data?.status || 'inactive',
    };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return defaultSubscriptionState;
  }
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const accessToken = session?.access_token ?? null;

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.subscription(userId),
    queryFn: () => fetchSubscriptionFields(accessToken as string),
    enabled: Boolean(userId && accessToken),
  });

  const subscriptionMutation = useMutation({
    mutationFn: fetchSubscriptionFields,
    onSuccess: (subscription) => {
      queryClient.setQueryData<SubscriptionFields>(
        queryKeys.subscription(userId),
        (previous) => ({ ...(previous ?? defaultSubscriptionState), ...subscription }),
      );
      if (userId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.subscription(userId) });
      }
    },
  });

  const checkSubscription = useCallback(async () => {
    if (!accessToken) {
      queryClient.setQueryData(queryKeys.subscription(userId), defaultSubscriptionState);
      return;
    }

    await subscriptionMutation.mutateAsync(accessToken);
  }, [accessToken, queryClient, subscriptionMutation, userId]);

  const state = userId
    ? subscriptionQuery.data ?? defaultSubscriptionState
    : defaultSubscriptionState;

  return (
    <SubscriptionContext.Provider
      value={{
        subscribed: state.subscribed,
        productId: state.productId,
        subscriptionEnd: state.subscriptionEnd,
        apiAddon: state.apiAddon,
        status: state.status,
        loading: Boolean(userId && accessToken && (subscriptionQuery.isLoading || subscriptionMutation.isPending)),
        checkSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
